import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useTheme } from "@mui/material/styles";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";

// chart.js — vẽ thẳng tại trang này thay vì qua thẻ biểu đồ mẫu của template
// (nền gradient, không có trục số). Xem ghi chú ở BieuDoCot/BieuDoThanh.
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip as ChartTooltip,
} from "chart.js";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDAlert from "components/MDAlert";
import MDBadge from "components/MDBadge";

import { nhanTrangThaiThanhToan } from "lib/nhanTrangThaiThanhToan";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import DauTrang, { NhanDau } from "components/DauTrang";
import TheChiSo from "components/TheChiSo";
import XemChungTuTrongApp from "components/XemChungTuTrongApp";
import { oTieuDeCot, AN_DUOI_MD, chuXuongDong } from "lib/kieuBangDuLieu";
import {
  useDialogFullManHinh,
  oDialogContentFormDai,
  oDialogPaperFormDai,
} from "lib/dungDialogFullManHinh";
import { LAYOUT } from "assets/theme/avocadoBrand";

import { giaoDichApi } from "api/giaoDich";
import useTour from "hooks/useTour";
import buocBaoCao from "tours/baoCao";

ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTooltip);

const CAO_BIEU_DO = 264;

function ngayISO(date) {
  return date.toISOString().slice(0, 10);
}

function homNay() {
  return ngayISO(new Date());
}

function soVN(n) {
  return (n ?? 0).toLocaleString("vi-VN");
}

// Gom danh sách giao dịch theo 1 khoá (nhân viên / loại bơ / chủ vườn+vườn),
// trả về mảng đã sắp xếp giảm dần theo thành tiền — dùng chung cho các bảng
// tổng hợp bên dưới. Giữ lại "ds" (các giao dịch gốc thuộc nhóm) để ấn vào
// hàng tổng hợp thì mở được danh sách PO cụ thể đứng sau con số đó — mỗi
// phần tử vẫn còn nguyên "items" đầy đủ của cả PO (kể cả khi ds tới từ
// danhSachTheoLoaiBo đã flatten theo dòng loại bơ), nên mở tiếp lên là xem
// được trọn vẹn PO chứ không chỉ riêng dòng loại bơ đó.
function nhomTheo(danhSach, layKhoa, layTen) {
  const nhom = {};
  danhSach.forEach((gd) => {
    const khoa = layKhoa(gd) ?? "__khac";
    if (!nhom[khoa]) nhom[khoa] = { ten: layTen(gd), soGiaoDich: 0, kg: 0, tien: 0, ds: [] };
    nhom[khoa].soGiaoDich += 1;
    nhom[khoa].kg += gd.khoiLuong;
    nhom[khoa].tien += gd.thanhTien;
    nhom[khoa].ds.push(gd);
  });
  return Object.values(nhom).sort((a, b) => b.tien - a.tien);
}

/** Khối rỗng dùng chung cho cả biểu đồ lẫn bảng khi khoảng ngày không có số liệu. */
function KhoiRong({ icon, tieuDe, moTa }) {
  return (
    <MDBox px={2.5} py={4} textAlign="center">
      <Icon sx={({ palette }) => ({ color: palette.grey[400], fontSize: "2.5rem" })}>{icon}</Icon>
      <MDTypography variant="button" fontWeight="medium" display="block" mt={1}>
        {tieuDe}
      </MDTypography>
      {moTa && (
        <MDTypography variant="caption" color="text" display="block" mt={0.5}>
          {moTa}
        </MDTypography>
      )}
    </MDBox>
  );
}

KhoiRong.propTypes = {
  icon: PropTypes.string.isRequired,
  tieuDe: PropTypes.string.isRequired,
  moTa: PropTypes.string,
};

KhoiRong.defaultProps = {
  moTa: null,
};

/** Vỏ thẻ chung cho 2 biểu đồ: nhãn nhỏ + tiêu đề + mô tả, gạch chỉ, rồi khung vẽ. */
function TheBieuDo({ nhan, tieuDe, moTa, children }) {
  return (
    <Card sx={{ height: "100%" }}>
      <MDBox px={2.5} pt={2.5} pb={1.5}>
        <NhanDau>{nhan}</NhanDau>
        <MDTypography variant="h6" component="h2">
          {tieuDe}
        </MDTypography>
        <MDTypography variant="caption" color="text">
          {moTa}
        </MDTypography>
      </MDBox>
      <Divider sx={{ my: 0 }} />
      {children}
    </Card>
  );
}

TheBieuDo.propTypes = {
  nhan: PropTypes.string.isRequired,
  tieuDe: PropTypes.string.isRequired,
  moTa: PropTypes.node.isRequired,
  children: PropTypes.node.isRequired,
};

function BangTongHop({ id, tieuDe, cotDau, danhSach, onChonHang }) {
  return (
    <Card id={id} sx={{ height: "100%" }}>
      <MDBox
        px={2.5}
        py={2}
        display="flex"
        flexWrap="wrap"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
      >
        <MDTypography variant="h6" component="h2">
          {tieuDe}
        </MDTypography>
        {danhSach.length > 0 && (
          <MDTypography variant="caption" color="text">
            {danhSach.length} dòng — bấm một dòng để xem giao dịch
          </MDTypography>
        )}
      </MDBox>
      {danhSach.length === 0 ? (
        <KhoiRong
          icon="summarize"
          tieuDe="Không có dữ liệu trong khoảng ngày đã chọn."
          moTa="Thử mở rộng khoảng ngày ở đầu trang."
        />
      ) : (
        <MDBox sx={{ overflowX: "auto" }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={oTieuDeCot}>{cotDau}</TableCell>
                <TableCell align="right" sx={[oTieuDeCot, AN_DUOI_MD]}>
                  Số giao dịch
                </TableCell>
                <TableCell align="right" sx={oTieuDeCot}>
                  Khối lượng
                </TableCell>
                <TableCell align="right" sx={oTieuDeCot}>
                  Thành tiền
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {danhSach.map((d) => (
                <TableRow
                  key={d.ten}
                  hover
                  onClick={() => onChonHang(d)}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell>
                    <MDTypography variant="button" fontWeight="medium" display="block">
                      {d.ten}
                    </MDTypography>
                    {/* Cột "Số giao dịch" bị ẩn dưới md — gom xuống dòng nhỏ này
                        để không mất dữ liệu, xem lib/kieuBangDuLieu. */}
                    <MDTypography
                      variant="caption"
                      color="text"
                      display={{ xs: "block", md: "none" }}
                      mt={0.25}
                    >
                      {d.soGiaoDich} giao dịch
                    </MDTypography>
                  </TableCell>
                  <TableCell align="right" sx={AN_DUOI_MD}>
                    {d.soGiaoDich}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    {soVN(d.kg)} kg
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    <MDTypography variant="button" fontWeight="medium">
                      {soVN(d.tien)} đ
                    </MDTypography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </MDBox>
      )}
    </Card>
  );
}

BangTongHop.propTypes = {
  id: PropTypes.string,
  tieuDe: PropTypes.string.isRequired,
  cotDau: PropTypes.string.isRequired,
  danhSach: PropTypes.arrayOf(
    PropTypes.shape({
      ten: PropTypes.string,
      soGiaoDich: PropTypes.number,
      kg: PropTypes.number,
      tien: PropTypes.number,
    })
  ).isRequired,
  onChonHang: PropTypes.func.isRequired,
};

BangTongHop.defaultProps = {
  id: undefined,
};

// Nhãn tỷ trọng vẽ ngay đầu mút mỗi thanh ngang — để đọc được "loại này chiếm
// bao nhiêu phần" mà không cần chú giải màu (biểu đồ chỉ dùng 1 màu, tên loại
// bơ nằm thẳng trên trục nên danh tính không phụ thuộc màu sắc).
const nhanCuoiThanh = {
  id: "nhanCuoiThanh",
  afterDatasetsDraw(chart, _args, tuyChon) {
    const nhan = tuyChon?.nhan ?? [];
    const { ctx } = chart;
    ctx.save();
    ctx.font = tuyChon.font;
    ctx.fillStyle = tuyChon.mau;
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    chart.getDatasetMeta(0).data.forEach((thanh, i) => {
      if (nhan[i]) ctx.fillText(nhan[i], thanh.x + 8, thanh.y);
    });
    ctx.restore();
  },
};

function BaoCao() {
  const { palette, typography } = useTheme();
  const fullManHinh = useDialogFullManHinh();

  const [tuNgay, setTuNgay] = useState(homNay);
  const [denNgay, setDenNgay] = useState(homNay);
  const [danhSach, setDanhSach] = useState([]);
  const [dangTai, setDangTai] = useState(true);
  const [loi, setLoi] = useState("");

  // nhomDangXem: nhóm vừa ấn ở 1 trong 3 bảng tổng hợp (Theo nhân viên/loại
  // bơ/chủ vườn) — mở popup liệt kê từng PO thuộc nhóm đó. poDangXem: PO vừa
  // ấn tiếp trong popup đó — mở popup thứ 2 xem trọn vẹn PO (đủ mọi loại bơ,
  // không chỉ riêng dòng dẫn tới đây).
  const [nhomDangXem, setNhomDangXem] = useState(null);
  const [poDangXem, setPoDangXem] = useState(null);
  // PO trải >1 sọt (khi 1 sọt đầy giữa chừng lúc cân, xem
  // giaoDichApi.taoMoi/suaPO) — chỉ hiện cột "Sọt" trong popup chi tiết PO
  // lúc cần, giữ bảng gọn như cũ với >95% PO chỉ dùng đúng 1 sọt.
  const coNhieuSotPoDangXem = (poDangXem?.danhSachSot?.length ?? 0) > 1;

  useEffect(() => {
    if (tuNgay > denNgay) {
      setLoi("Từ ngày phải trước hoặc bằng Đến ngày.");
      return undefined;
    }
    setDangTai(true);
    setLoi("");
    const huy = giaoDichApi.langNgheTrongKhoang(
      tuNgay,
      denNgay,
      (ds) => {
        setDanhSach(ds);
        setDangTai(false);
      },
      (err) => {
        setLoi(err.message);
        setDangTai(false);
      }
    );
    return huy;
  }, [tuNgay, denNgay]);

  // "Số giao dịch" = số PO (lượt thu mua) — danhSach.length đã đúng nghĩa vì
  // mỗi PO giờ là 1 phần tử. Theo nhân viên/vườn/ngày vẫn tính ở cấp PO (1 PO
  // nhiều loại bơ vẫn là 1 giao dịch), nên cộng kg/tiền qua toàn bộ items[]
  // của từng PO thay vì đọc thẳng field đã không còn ở gốc nữa.
  const danhSachTong = useMemo(
    () =>
      danhSach.map((gd) => ({
        ...gd,
        khoiLuong: gd.items.reduce((s, it) => s + it.khoiLuong, 0),
        thanhTien: gd.items.reduce((s, it) => s + it.thanhTien, 0),
      })),
    [danhSach]
  );
  // Riêng "theo loại bơ" cần tách từng dòng loại bơ ra khỏi PO — 1 PO 2 loại
  // bơ phải tính vào 2 nhóm khác nhau, không gộp chung theo PO được.
  const danhSachTheoLoaiBo = useMemo(
    () => danhSach.flatMap((gd) => gd.items.map((it) => ({ ...gd, ...it }))),
    [danhSach]
  );

  const tongKg = useMemo(() => danhSachTong.reduce((s, gd) => s + gd.khoiLuong, 0), [danhSachTong]);
  const tongTien = useMemo(
    () => danhSachTong.reduce((s, gd) => s + gd.thanhTien, 0),
    [danhSachTong]
  );
  const soChuVuon = useMemo(
    () => new Set(danhSach.map((gd) => gd.maChuVuon).filter(Boolean)).size,
    [danhSach]
  );

  const theoNgay = useMemo(() => {
    const nhom = {};
    danhSachTong.forEach((gd) => {
      const ngay = gd.thoiGian.slice(0, 10);
      nhom[ngay] = nhom[ngay] ?? { ngay, kg: 0, tien: 0 };
      nhom[ngay].kg += gd.khoiLuong;
      nhom[ngay].tien += gd.thanhTien;
    });
    return Object.values(nhom).sort((a, b) => (a.ngay < b.ngay ? -1 : 1));
  }, [danhSachTong]);

  const theoNhanVien = useMemo(
    () =>
      nhomTheo(
        danhSachTong,
        (gd) => gd.maNhanVien,
        (gd) => gd.tenNhanVien ?? "—"
      ),
    [danhSachTong]
  );
  const theoLoaiBo = useMemo(
    () =>
      nhomTheo(
        danhSachTheoLoaiBo,
        (gd) => gd.maLoaiBo,
        (gd) => gd.tenLoaiBo ?? "—"
      ),
    [danhSachTheoLoaiBo]
  );
  const theoVuon = useMemo(
    () =>
      nhomTheo(
        danhSachTong,
        (gd) => `${gd.maChuVuon}__${gd.maVuon}`,
        (gd) => `${gd.tenChuVuon ?? "—"} — ${gd.tenVuon ?? "—"}`
      ),
    [danhSachTong]
  );

  // Kiểu chữ/màu dùng chung cho cả 2 biểu đồ — đọc từ theme để trục số, nhãn
  // và lưới cùng ngôn ngữ với phần còn lại của trang.
  const chuTruc = useMemo(
    () => ({ family: typography.fontFamily, size: 11 }),
    [typography.fontFamily]
  );
  const tuyChonTooltip = useMemo(
    () => ({
      backgroundColor: palette.dark.main,
      titleFont: { family: typography.fontFamily, size: 12, weight: "600" },
      bodyFont: { family: typography.fontFamily, size: 12 },
      padding: 10,
      cornerRadius: 8,
      displayColors: false,
    }),
    [palette, typography.fontFamily]
  );

  // Biểu đồ 1 — cột dọc theo ngày: một chuỗi số liệu duy nhất nên không cần
  // chú giải, tiêu đề thẻ đã nói rõ đang đo cái gì.
  const duLieuTheoNgay = useMemo(
    () => ({
      labels: theoNgay.map((d) => `${d.ngay.slice(8, 10)}/${d.ngay.slice(5, 7)}`),
      datasets: [
        {
          data: theoNgay.map((d) => d.kg),
          backgroundColor: palette.info.main,
          hoverBackgroundColor: palette.info.focus,
          borderRadius: 4,
          borderSkipped: false,
          maxBarThickness: 36,
          categoryPercentage: 0.82,
          barPercentage: 0.86,
        },
      ],
    }),
    [theoNgay, palette]
  );

  const tuyChonTheoNgay = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          ...tuyChonTooltip,
          callbacks: {
            label: (ct) => `${soVN(ct.parsed.y)} kg`,
            afterLabel: (ct) => `${soVN(theoNgay[ct.dataIndex]?.tien)} đ`,
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          border: { color: palette.grey[300] },
          ticks: { color: palette.text.main, font: chuTruc },
        },
        y: {
          beginAtZero: true,
          grid: { color: palette.grey[200], drawTicks: false },
          border: { display: false },
          ticks: {
            color: palette.text.main,
            font: chuTruc,
            maxTicksLimit: 5,
            padding: 8,
            callback: (v) => soVN(v),
          },
        },
      },
    }),
    [palette, chuTruc, tuyChonTooltip, theoNgay]
  );

  // Biểu đồ 2 — thanh ngang xếp hạng theo loại bơ. Thanh ngang (không phải
  // hình tròn) vì tên loại bơ đọc thẳng trên trục, so sánh độ dài dễ hơn so
  // sánh góc, và chỉ cần 1 màu nên không phát sinh bảng màu phân loại.
  const tongKgLoaiBo = useMemo(() => theoLoaiBo.reduce((s, d) => s + d.kg, 0), [theoLoaiBo]);

  const duLieuTheoLoaiBo = useMemo(
    () => ({
      labels: theoLoaiBo.map((d) => d.ten),
      datasets: [
        {
          data: theoLoaiBo.map((d) => d.kg),
          backgroundColor: palette.info.main,
          hoverBackgroundColor: palette.info.focus,
          borderRadius: 4,
          borderSkipped: false,
          maxBarThickness: 22,
          categoryPercentage: 0.8,
          barPercentage: 0.9,
        },
      ],
    }),
    [theoLoaiBo, palette]
  );

  const tuyChonTheoLoaiBo = useMemo(
    () => ({
      indexAxis: "y",
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { right: 46 } },
      plugins: {
        legend: { display: false },
        tooltip: {
          ...tuyChonTooltip,
          callbacks: {
            label: (ct) => `${soVN(ct.parsed.x)} kg`,
            afterLabel: (ct) => `${soVN(theoLoaiBo[ct.dataIndex]?.tien)} đ`,
          },
        },
        nhanCuoiThanh: {
          font: `600 11px ${typography.fontFamily}`,
          mau: palette.text.main,
          nhan: theoLoaiBo.map((d) =>
            tongKgLoaiBo > 0 ? `${Math.round((d.kg / tongKgLoaiBo) * 100)}%` : ""
          ),
        },
      },
      scales: {
        x: {
          beginAtZero: true,
          grid: { color: palette.grey[200], drawTicks: false },
          border: { display: false },
          ticks: {
            color: palette.text.main,
            font: chuTruc,
            maxTicksLimit: 5,
            padding: 6,
            callback: (v) => soVN(v),
          },
        },
        y: {
          grid: { display: false },
          border: { color: palette.grey[300] },
          ticks: { color: palette.text.main, font: chuTruc, autoSkip: false, crossAlign: "far" },
        },
      },
    }),
    [palette, typography.fontFamily, chuTruc, tuyChonTooltip, theoLoaiBo, tongKgLoaiBo]
  );

  const khoangNgayHienThi =
    tuNgay === denNgay
      ? new Date(tuNgay).toLocaleDateString("vi-VN")
      : `${new Date(tuNgay).toLocaleDateString("vi-VN")} → ${new Date(denNgay).toLocaleDateString(
          "vi-VN"
        )}`;

  useTour("quan-ly-bao-cao", dangTai ? [] : buocBaoCao());

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <DauTrang
          nhan="Báo cáo"
          tieuDe="Báo cáo thu mua"
          moTa="Tổng hợp theo ngày, nhân viên, loại bơ và chủ vườn."
        >
          <MDBox
            id="tour-quan-ly-bao-cao-bo-loc"
            display="flex"
            flexWrap="wrap"
            alignItems="flex-end"
            gap={2}
          >
            <MDBox
              sx={{
                width: { xs: "calc(50% - 8px)", sm: LAYOUT.filterSelectWidth },
                flex: { sm: `0 0 ${LAYOUT.filterSelectWidth}px` },
              }}
            >
              <MDInput
                type="date"
                label="Từ ngày"
                value={tuNgay}
                onChange={(e) => setTuNgay(e.target.value)}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </MDBox>
            <MDBox
              sx={{
                width: { xs: "calc(50% - 8px)", sm: LAYOUT.filterSelectWidth },
                flex: { sm: `0 0 ${LAYOUT.filterSelectWidth}px` },
              }}
            >
              <MDInput
                type="date"
                label="Đến ngày"
                value={denNgay}
                onChange={(e) => setDenNgay(e.target.value)}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </MDBox>
          </MDBox>
        </DauTrang>

        {loi && (
          <MDBox mb={2}>
            <MDAlert color="error">{loi}</MDAlert>
          </MDBox>
        )}

        {dangTai ? (
          <MDTypography variant="button" color="text">
            Đang tải...
          </MDTypography>
        ) : (
          <>
            {/* Bốn chỉ số đọc thành ba nhóm: khối lượng thu về (xanh lá), tiền
                đã trả (vàng hạt bơ), và quy mô lượt/người (xanh vỏ bơ). */}
            <Grid id="tour-quan-ly-bao-cao-chi-so" container spacing={2.5} mb={3}>
              <Grid item xs={6} lg={3}>
                <TheChiSo
                  nhan="Số giao dịch"
                  giaTri={danhSach.length}
                  donVi="phiếu thu mua"
                  icon="receipt_long"
                  mau={palette.primary.main}
                />
              </Grid>
              <Grid item xs={6} lg={3}>
                <TheChiSo
                  nhan="Khối lượng"
                  giaTri={`${soVN(tongKg)} kg`}
                  donVi="đã cân trong khoảng"
                  icon="scale"
                  mau={palette.info.main}
                />
              </Grid>
              <Grid item xs={6} lg={3}>
                <TheChiSo
                  nhan="Thành tiền"
                  giaTri={soVN(tongTien)}
                  donVi="đồng"
                  icon="payments"
                  mau={palette.gradients.avocado.main}
                />
              </Grid>
              <Grid item xs={6} lg={3}>
                <TheChiSo
                  nhan="Chủ vườn"
                  giaTri={soChuVuon}
                  donVi="đã bán trong khoảng"
                  icon="groups"
                  mau={palette.primary.main}
                />
              </Grid>
            </Grid>

            <Grid id="tour-quan-ly-bao-cao-bieu-do" container spacing={3} mb={3}>
              <Grid item xs={12} lg={7}>
                <TheBieuDo
                  nhan="Theo thời gian"
                  tieuDe="Khối lượng thu mua mỗi ngày"
                  moTa={khoangNgayHienThi}
                >
                  {theoNgay.length === 0 ? (
                    <KhoiRong
                      icon="query_stats"
                      tieuDe="Chưa có ngày nào có số liệu."
                      moTa="Thử mở rộng khoảng ngày ở đầu trang."
                    />
                  ) : (
                    <MDBox px={2} py={2} height={CAO_BIEU_DO}>
                      <Bar data={duLieuTheoNgay} options={tuyChonTheoNgay} />
                    </MDBox>
                  )}
                </TheBieuDo>
              </Grid>
              <Grid item xs={12} lg={5}>
                <TheBieuDo
                  nhan="Tỷ trọng"
                  tieuDe="Khối lượng theo loại bơ"
                  moTa={`${soVN(tongKgLoaiBo)} kg — số ở đầu thanh là phần trăm tổng`}
                >
                  {theoLoaiBo.length === 0 ? (
                    <KhoiRong
                      icon="donut_large"
                      tieuDe="Chưa có loại bơ nào được thu mua."
                      moTa="Thử mở rộng khoảng ngày ở đầu trang."
                    />
                  ) : (
                    <MDBox px={2} py={2} height={CAO_BIEU_DO}>
                      <Bar
                        data={duLieuTheoLoaiBo}
                        options={tuyChonTheoLoaiBo}
                        plugins={[nhanCuoiThanh]}
                      />
                    </MDBox>
                  )}
                </TheBieuDo>
              </Grid>
            </Grid>

            <Grid container spacing={3} mb={3}>
              <Grid item xs={12} lg={6}>
                <BangTongHop
                  id="tour-quan-ly-bao-cao-bang"
                  tieuDe="Theo nhân viên"
                  cotDau="Nhân viên"
                  danhSach={theoNhanVien}
                  onChonHang={setNhomDangXem}
                />
              </Grid>
              <Grid item xs={12} lg={6}>
                <BangTongHop
                  tieuDe="Theo loại bơ"
                  cotDau="Loại bơ"
                  danhSach={theoLoaiBo}
                  onChonHang={setNhomDangXem}
                />
              </Grid>
            </Grid>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <BangTongHop
                  tieuDe="Theo chủ vườn / vườn"
                  cotDau="Chủ vườn — Vườn"
                  danhSach={theoVuon}
                  onChonHang={setNhomDangXem}
                />
              </Grid>
            </Grid>
          </>
        )}
      </MDBox>

      <Dialog
        open={!!nhomDangXem}
        onClose={() => setNhomDangXem(null)}
        fullScreen={fullManHinh}
        maxWidth="md"
        fullWidth
        PaperProps={oDialogPaperFormDai}
      >
        {nhomDangXem && (
          <>
            <DialogTitle component="div" sx={{ pb: 0.5, pr: 6, flexShrink: 0, ...chuXuongDong }}>
              <NhanDau>Chi tiết nhóm</NhanDau>
              <MDTypography variant="h5" component="h2" sx={chuXuongDong}>
                {nhomDangXem.ten}
              </MDTypography>
              <MDTypography variant="caption" color="text" sx={chuXuongDong}>
                {nhomDangXem.soGiaoDich} giao dịch · {soVN(nhomDangXem.kg)} kg ·{" "}
                {soVN(nhomDangXem.tien)} đ
              </MDTypography>
              <IconButton
                onClick={() => setNhomDangXem(null)}
                sx={{ position: "absolute", right: 8, top: 8 }}
              >
                <Icon>close</Icon>
              </IconButton>
            </DialogTitle>
            <DialogContent sx={oDialogContentFormDai}>
              <MDBox sx={{ overflowX: "auto" }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={oTieuDeCot}>Ngày</TableCell>
                      <TableCell sx={oTieuDeCot}>Chủ vườn — Vườn</TableCell>
                      <TableCell sx={[oTieuDeCot, AN_DUOI_MD]}>Nhân viên</TableCell>
                      <TableCell align="right" sx={oTieuDeCot}>
                        Khối lượng
                      </TableCell>
                      <TableCell align="right" sx={oTieuDeCot}>
                        Thành tiền
                      </TableCell>
                      <TableCell sx={[oTieuDeCot, AN_DUOI_MD]}>Thanh toán</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {nhomDangXem.ds.map((gd, idx) => {
                      const trangThai = nhanTrangThaiThanhToan(gd.trangThaiThanhToan);
                      return (
                        // gd.id lặp lại khi nhóm tới từ danhSachTheoLoaiBo và
                        // 1 PO có 2 dòng cùng loại bơ (hiếm nhưng có thể) —
                        // ghép thêm idx cho chắc key không trùng.
                        <TableRow
                          key={`${gd.id}-${idx}`}
                          hover
                          onClick={() => setPoDangXem(gd)}
                          sx={{ cursor: "pointer" }}
                        >
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {new Date(gd.thoiGian).toLocaleString("vi-VN")}
                          </TableCell>
                          <TableCell sx={chuXuongDong}>
                            <MDTypography
                              variant="button"
                              fontWeight="medium"
                              display="block"
                              sx={chuXuongDong}
                            >
                              {gd.tenChuVuon ?? "—"} — {gd.tenVuon ?? "—"}
                            </MDTypography>
                            <MDTypography
                              variant="caption"
                              color="text"
                              display={{ xs: "block", md: "none" }}
                              mt={0.25}
                              sx={chuXuongDong}
                            >
                              {gd.tenNhanVien ?? "—"}
                              {trangThai?.label ? ` · ${trangThai.label}` : ""}
                            </MDTypography>
                          </TableCell>
                          <TableCell sx={AN_DUOI_MD}>{gd.tenNhanVien ?? "—"}</TableCell>
                          <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                            {soVN(gd.khoiLuong)} kg
                          </TableCell>
                          <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                            {soVN(gd.thanhTien)} đ
                          </TableCell>
                          <TableCell sx={AN_DUOI_MD}>
                            <MDBadge
                              badgeContent={trangThai.label}
                              color={trangThai.color}
                              variant="gradient"
                              size="sm"
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </MDBox>
            </DialogContent>
          </>
        )}
      </Dialog>

      <Dialog
        open={!!poDangXem}
        onClose={() => setPoDangXem(null)}
        fullScreen={fullManHinh}
        maxWidth="md"
        fullWidth
        PaperProps={oDialogPaperFormDai}
      >
        {poDangXem && (
          <>
            <DialogTitle component="div" sx={{ pb: 0.5, pr: 6, flexShrink: 0, ...chuXuongDong }}>
              <NhanDau>Phiếu thu mua</NhanDau>
              <MDTypography variant="h5" component="h2" sx={chuXuongDong}>
                {poDangXem.tenChuVuon ?? "—"} — {poDangXem.tenVuon ?? "—"}
              </MDTypography>
              <MDTypography variant="caption" color="text">
                {new Date(poDangXem.thoiGian).toLocaleString("vi-VN")}
              </MDTypography>
              <IconButton
                onClick={() => setPoDangXem(null)}
                sx={{ position: "absolute", right: 8, top: 8 }}
              >
                <Icon>close</Icon>
              </IconButton>
            </DialogTitle>
            <DialogContent sx={oDialogContentFormDai}>
              <MDBox
                mb={2}
                px={1.75}
                py={1.25}
                borderRadius="lg"
                sx={({ palette: mau }) => ({
                  backgroundColor: mau.grey[100],
                  borderLeft: `3px solid ${mau.info.main}`,
                })}
              >
                <MDTypography variant="caption" color="text" display="block">
                  Nhân viên: {poDangXem.tenNhanVien ?? "—"}
                </MDTypography>
                {poDangXem.maSotHienThi && (
                  <MDTypography variant="caption" color="text" display="block">
                    Sọt: {poDangXem.maSotHienThi}
                  </MDTypography>
                )}
                <MDBox mt={0.75}>
                  <MDBadge
                    badgeContent={nhanTrangThaiThanhToan(poDangXem.trangThaiThanhToan).label}
                    color={nhanTrangThaiThanhToan(poDangXem.trangThaiThanhToan).color}
                    variant="gradient"
                    size="sm"
                  />
                </MDBox>
              </MDBox>

              <MDBox sx={{ overflowX: "auto" }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={oTieuDeCot}>Loại bơ</TableCell>
                      {coNhieuSotPoDangXem && <TableCell sx={oTieuDeCot}>Sọt</TableCell>}
                      <TableCell align="right" sx={oTieuDeCot}>
                        Khối lượng
                      </TableCell>
                      <TableCell align="right" sx={oTieuDeCot}>
                        Đơn giá
                      </TableCell>
                      <TableCell align="right" sx={oTieuDeCot}>
                        Thành tiền
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(poDangXem.items ?? []).map((it, idx) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <TableRow key={idx}>
                        <TableCell>{it.tenLoaiBo ?? "—"}</TableCell>
                        {coNhieuSotPoDangXem && <TableCell>{it.maSotHienThi ?? "—"}</TableCell>}
                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                          {soVN(it.khoiLuong)} kg
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                          {soVN(it.donGia)} đ/kg
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                          {soVN(it.thanhTien)} đ
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </MDBox>

              {poDangXem.ghiChu && (
                <MDBox mt={2}>
                  <MDTypography variant="caption" color="text">
                    Ghi chú: {poDangXem.ghiChu}
                  </MDTypography>
                </MDBox>
              )}

              {poDangXem.chungTuUrl && (
                <MDBox mt={2}>
                  <XemChungTuTrongApp url={poDangXem.chungTuUrl} />
                </MDBox>
              )}
            </DialogContent>
          </>
        )}
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default BaoCao;
